import React from 'react';
import './PasswordStrength.css';

export interface PasswordValidation {
  minLength: boolean;
  hasUpperCase: boolean;
  hasLowerCase: boolean;
  hasNumber: boolean;
  hasSymbol: boolean;
  score: number; // 0-4
}

interface PasswordStrengthProps {
  password: string;
  showValidation?: boolean;
}

export const validatePassword = (password: string): PasswordValidation => {
  const validation = {
    minLength: password.length >= 8,
    hasUpperCase: /[A-Z]/.test(password),
    hasLowerCase: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSymbol: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password),
    score: 0,
  };

  // Calculate score
  if (validation.minLength) validation.score++;
  if (validation.hasUpperCase) validation.score++;
  if (validation.hasLowerCase) validation.score++;
  if (validation.hasNumber) validation.score++;
  if (validation.hasSymbol) validation.score++;

  return validation;
};

const PasswordStrength: React.FC<PasswordStrengthProps> = ({ password, showValidation = true }) => {
  const validation = validatePassword(password);

  const getStrengthLabel = (): string => {
    if (!password) return '';
    if (validation.score <= 2) return 'Weak';
    if (validation.score === 3) return 'Fair';
    if (validation.score === 4) return 'Good';
    return 'Strong';
  };

  const getStrengthClass = (): string => {
    if (!password) return '';
    if (validation.score <= 2) return 'weak';
    if (validation.score === 3) return 'fair';
    if (validation.score === 4) return 'good';
    return 'strong';
  };

  if (!password) return null;

  return (
    <div className="password-strength">
      <div className="strength-bar-container">
        <div className={`strength-bar strength-${getStrengthClass()}`}>
          <div
            className="strength-bar-fill"
            style={{ width: `${(validation.score / 5) * 100}%` }}
          />
        </div>
        <span className={`strength-label strength-${getStrengthClass()}`}>
          {getStrengthLabel()}
        </span>
      </div>

      {showValidation && (
        <div className="validation-rules">
          <div className={`validation-rule ${validation.minLength ? 'valid' : 'invalid'}`}>
            <span className="validation-icon">{validation.minLength ? '✓' : '×'}</span>
            At least 8 characters
          </div>
          <div className={`validation-rule ${validation.hasUpperCase ? 'valid' : 'invalid'}`}>
            <span className="validation-icon">{validation.hasUpperCase ? '✓' : '×'}</span>
            One uppercase letter
          </div>
          <div className={`validation-rule ${validation.hasLowerCase ? 'valid' : 'invalid'}`}>
            <span className="validation-icon">{validation.hasLowerCase ? '✓' : '×'}</span>
            One lowercase letter
          </div>
          <div className={`validation-rule ${validation.hasNumber ? 'valid' : 'invalid'}`}>
            <span className="validation-icon">{validation.hasNumber ? '✓' : '×'}</span>
            One number
          </div>
          <div className={`validation-rule ${validation.hasSymbol ? 'valid' : 'invalid'}`}>
            <span className="validation-icon">{validation.hasSymbol ? '✓' : '×'}</span>
            One symbol (!@#$%^&*)
          </div>
        </div>
      )}
    </div>
  );
};

export default PasswordStrength;
